import { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import { AuthorityCard } from '@/components/territories/AuthorityCard';
import { AuthorityAssignmentDialog } from '@/components/territories/AuthorityAssignmentDialog';
import { useAuth } from '@/context/authContext';
import { isAdmin, type User } from '@/models/user';
import { getUserProfileByAuthId } from '@/Services/users.service';
import { updateDistrictAuthorities, updateRegionAuthorities, getRegionsWithStatsForDistrict, getAssignedMinisterIds } from '@/data/territoryRepository';
import type { District } from '@/models/district';
import type { Region } from '@/models/region';
import type { Minister } from '@/models/minister';

interface TerritoryContext {
  district: District | null;
  region: Region | null;
  isDistrict: boolean;
  refreshTerritory: () => Promise<void>;
}

type AuthorityPosition = {
  key: string;
  label: string;
  minister: Minister | null | undefined;
};

const DISTRICT_POSITIONS = [
  { key: 'superintendent', label: 'Superintendente' },
  { key: 'vice_superintendent', label: 'Vice Superintendente' },
  { key: 'secretary', label: 'Secretario' },
  { key: 'treasurer', label: 'Tesorero' },
  { key: 'vocal1', label: 'Vocal 1' },
  { key: 'vocal2', label: 'Vocal 2' },
];

const REGION_POSITIONS = [
  { key: 'presbyter', label: 'Presbítero' },
  { key: 'vice_presbyter', label: 'Vice Presbítero' },
  { key: 'secretary', label: 'Secretario' },
  { key: 'treasurer', label: 'Tesorero' },
  { key: 'vocal1', label: 'Vocal 1' },
  { key: 'vocal2', label: 'Vocal 2' },
];

export default function InformationTab() {
  const { district, region, isDistrict, refreshTerritory } = useOutletContext<TerritoryContext>();
  const { user: authUser } = useAuth();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const userFetchedRef = useRef(false);
  const lastDistrictIdRef = useRef<string | null>(null);
  const lastRegionIdRef = useRef<string | null>(null);
  const [regions, setRegions] = useState<(Region & { activeMemberCount: number })[]>([]);
  const [loadingRegions, setLoadingRegions] = useState(false);
  const [assignedMinisterIds, setAssignedMinisterIds] = useState<string[]>([]);
  const [assignmentDialog, setAssignmentDialog] = useState<{
    isOpen: boolean;
    position: AuthorityPosition | null;
  }>({ isOpen: false, position: null });
  
  const territory = isDistrict ? district : region;
  const userIsAdmin = isAdmin(currentUser);
  
  useEffect(() => {
    const fetchUser = async () => {
      if (!authUser || userFetchedRef.current) return;
      
      userFetchedRef.current = true;
      try {
        const userData = await getUserProfileByAuthId(authUser.id);
        setCurrentUser(userData);
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    fetchUser();
  }, [authUser]);
  
  // Load regions for districts
  useEffect(() => {
    const loadRegions = async () => {
      if (!isDistrict || !district?.id) return;
      
      setLoadingRegions(true);
      try {
        const regionsData = await getRegionsWithStatsForDistrict(district.id);
        setRegions(regionsData);
      } catch (error) {
        console.error('Error loading regions:', error);
      } finally {
        setLoadingRegions(false);
      }
    };
    loadRegions();
  }, [isDistrict, district?.id]);
  
  // Load assigned minister IDs based on scope
  useEffect(() => {
    const loadAssignedIds = async () => {
      if (isDistrict) {
        // District scope: get all authorities across district and its regions
        const districtId = district?.id;
        if (!districtId) return;
        
        // Prevent duplicate calls for the same district
        if (lastDistrictIdRef.current === districtId) return;
        lastDistrictIdRef.current = districtId;
        
        try {
          const ids = await getAssignedMinisterIds(districtId);
          setAssignedMinisterIds(ids);
        } catch (error) {
          console.error('Error loading assigned minister IDs:', error);
        }
      } else {
        // Region scope: need current region's authorities + parent district's authorities
        // This prevents selecting a minister who is already a district authority
        if (!region?.id) return;
        
        // Prevent duplicate calls for the same region
        if (lastRegionIdRef.current === region.id) return;
        lastRegionIdRef.current = region.id;
        
        const allIds: string[] = [];
        
        // Extract all authority IDs from current region
        if (region.presbyter_id) allIds.push(region.presbyter_id);
        if (region.vice_presbyter_id) allIds.push(region.vice_presbyter_id);
        if (region.secretary_id) allIds.push(region.secretary_id);
        if (region.treasurer_id) allIds.push(region.treasurer_id);
        if (region.vocal1_id) allIds.push(region.vocal1_id);
        if (region.vocal2_id) allIds.push(region.vocal2_id);
        
        // Fetch parent district authorities to prevent selecting district authorities for region positions
        if (region.district_id) {
          try {
            const districtIds = await getAssignedMinisterIds(region.district_id);
            allIds.push(...districtIds.filter(id => !allIds.includes(id)));
          } catch (error) {
            console.error('Error loading district authorities:', error);
          }
        }
        
        setAssignedMinisterIds(allIds);
      }
    };
    loadAssignedIds();
  }, [isDistrict, district?.id, region]);
  
  if (!territory) {
    return null;
  }
  
  const positions: AuthorityPosition[] = isDistrict
    ? DISTRICT_POSITIONS.map(pos => ({
        key: pos.key,
        label: pos.label,
        minister: (territory as District)[pos.key as keyof District] as Minister | undefined,
      }))
    : REGION_POSITIONS.map(pos => ({
        key: pos.key,
        label: pos.label,
        minister: (territory as Region)[pos.key as keyof Region] as Minister | undefined,
      }));
  
  const handleOpenAssignment = (position: AuthorityPosition) => {
    setAssignmentDialog({ isOpen: true, position });
  };
  
  const handleCloseAssignment = () => {
    setAssignmentDialog({ isOpen: false, position: null });
  };
  
  const handleSaveAssignment = async (ministerId: string | null, startedAt: string) => {
    if (!assignmentDialog.position || !territory) return;

    const positionKey = assignmentDialog.position.key;
    const updates = {
      [`${positionKey}_id`]: ministerId,
    };

    try {
      if (isDistrict) {
        await updateDistrictAuthorities(territory.id, updates, startedAt);
      } else {
        await updateRegionAuthorities(territory.id, updates, startedAt);
      }
      
      await refreshTerritory();
      
      // Reload assigned minister IDs after update
      if (isDistrict) {
        // District: reload all district+regions authorities
        const ids = await getAssignedMinisterIds(territory.id);
        setAssignedMinisterIds(ids);
      }
      // Region: assigned IDs will be updated via refreshTerritory -> region object change -> useEffect
    } catch (error) {
      console.error('Error saving authority assignment:', error);
      alert('Error al guardar la asignación. Por favor, intenta de nuevo.');
    }
  };
  
  return (
    <div className="p-6">
      <div className="mx-auto max-w-6xl space-y-6">
        {/* Authorities Section */}
        <div className="rounded-lg border border-border bg-card p-6">
          <h2 className="mb-6 text-lg font-semibold">Autoridades</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {positions.map((position) => (
              <AuthorityCard
                key={position.key}
                positionLabel={position.label}
                minister={position.minister}
                isAdmin={userIsAdmin}
                onAssign={() => handleOpenAssignment(position)}
              />
            ))}
          </div>
        </div>
        
        {/* Regions Table (Districts Only) */}
        {isDistrict && (
          <div className="rounded-lg border border-border bg-card p-6">
            <h2 className="mb-6 text-lg font-semibold">Regiones del Distrito</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="border-b border-border">
                    <th className="px-4 py-3 text-left text-sm font-semibold">Código</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Nombre</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">Miembros Activos</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Presbítero</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Vice Presbítero</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingRegions ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-sm text-muted-foreground">
                        Cargando regiones...
                      </td>
                    </tr>
                  ) : regions.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="px-4 py-8 text-center text-sm text-muted-foreground">
                        No hay regiones en este distrito
                      </td>
                    </tr>
                  ) : (
                    regions.map((reg) => (
                      <tr key={reg.id} className="border-b border-border hover:bg-muted/50">
                        <td className="px-4 py-3 text-sm font-medium">{reg.code}</td>
                        <td className="px-4 py-3 text-sm">{reg.name}</td>
                        <td className="px-4 py-3 text-center text-sm">{reg.activeMemberCount}</td>
                        <td className="px-4 py-3 text-sm">
                          {reg.presbyter ? (
                            <div>
                              <p className="font-medium">{reg.presbyter.adb_code}</p>
                              <p className="text-xs text-muted-foreground">{reg.presbyter.full_name}</p>
                            </div>
                          ) : (
                            <span className="italic text-muted-foreground">Sin asignar</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {reg.vice_presbyter ? (
                            <div>
                              <p className="font-medium">{reg.vice_presbyter.adb_code}</p>
                              <p className="text-xs text-muted-foreground">{reg.vice_presbyter.full_name}</p>
                            </div>
                          ) : (
                            <span className="italic text-muted-foreground">Sin asignar</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
      
      {assignmentDialog.position && (
        <AuthorityAssignmentDialog
          isOpen={assignmentDialog.isOpen}
          onClose={handleCloseAssignment}
          positionLabel={assignmentDialog.position.label}
          currentMinister={assignmentDialog.position.minister}
          onSave={handleSaveAssignment}
          districtId={isDistrict ? territory.id : (region?.district_id ?? null)}
          regionId={isDistrict ? null : territory.id}
          assignedMinisterIds={assignedMinisterIds}
        />
      )}
    </div>
  );
}
